// @ts-strict-ignore
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, Signal, viewChild } from '@angular/core';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { WagoApiStatusCode } from '@shared/service/wago-api-status-code';
import { UserModel } from '@user/model/user.model';
import { AlertHelper } from '@shared/alert/alert.helper';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ArrayUtils } from '@shared/util/array-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { ProjectGroupUtils } from '@project/components/project/util/project-group-utils';
import { CustomValidators } from '@shared/custom-validators';
import { ProjectGroupService } from '../../../service/project-group.service';
import { Log } from '@shared/debug/log';
import { StringUtils } from '@shared/util/string-utils';
import { UserInfoVO } from '../../../model/vo/project.vo';

@Component({
  standalone: false,
  selector: 'app-edit-project-group',
  templateUrl: './edit-project-group.component.html',
})
export class EditProjectGroupComponent implements AfterViewInit {

  public readonly MAX_GROUP_NAME_LENGTH: number =  ProjectGroupUtils.MAX_GROUP_NAME_LENGTH;

  @Input({required: true}) public title: string;
  @Input({required: true}) public parentGroup: ProjectGroupVO;
  @Input() public groupType: number = ProjectGroupUtils.FOLDER;
  @Input({required: true}) public projectGroup: ProjectGroupVO;
  @Input({required: true}) public isArticleProjectGroup: boolean;
  @Input({required: true}) public isHidden: boolean;
  @Output() public finish = new EventEmitter<ProjectGroupVO>();

  public groupNameError: string = null;

  private groupNameTextInput: Signal<ElementRef> = viewChild('groupNameTextInput');

  constructor(
    private bsModalRef: BsModalRef,
    private userModel: UserModel,
    private alertHelper: AlertHelper,
    private projectGroupService: ProjectGroupService,
  ) { }

  public ngAfterViewInit(): void {
    if (this.projectGroup) {
      if (this.groupNameTextInput()) {
        this.groupNameTextInput().nativeElement.value = this.projectGroup.groupName;
      }
      this.groupType = this.projectGroup.groupType;
    }
    setTimeout(() => {
      this.groupNameTextInput()?.nativeElement.focus();
      this.groupNameTextInput()?.nativeElement.select();
    }, 250);
  }

  public isConfigurationGroupType(): boolean {
    return ProjectGroupUtils.isConfigurationGroupType(this.groupType);
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public save(): Promise<void> {
    if (!this.groupNameTextInput()?.nativeElement.value.trim()) {
      return;
    }
    return this.saveGroup();
  }

  private async saveGroup(): Promise<void> {
    this.createNewProjectGroupIfNecessary();
    if (!this.projectGroup.parentGroup) {
      try {
        const baseGroups = await this.projectGroupService.loadBaseProjectGroups(this.isArticleProjectGroup);
        await this.checkForDuplicatesAndContinueToSave(baseGroups);
      } catch (error) {
        Log.warn(error);
      }
    } else {
      try {
        const projectGroup = await this.projectGroupService.loadProjectGroupById(this.isArticleProjectGroup, this.projectGroup.parentGroup.id);
        let projectGroupsToCheck: ProjectGroupVO[] = [];
        if (projectGroup && ArrayUtils.isNotEmpty(projectGroup.groups)) {
          projectGroupsToCheck = projectGroup.groups;
        }
        await this.checkForDuplicatesAndContinueToSave(projectGroupsToCheck);
      } catch (error) {
        Log.warn(error);
      }
    }
  }

  private createNewProjectGroupIfNecessary() {
    let userId: number = this.userModel.userProfile.user.id;
    let username: string = this.userModel.userProfile.user.login;
    if (this.parentGroup) {
      if (NumberUtils.isNumber(this.parentGroup.groupOwner.userId) && this.parentGroup.groupOwner.userId !== -1 && this.userModel.userProfile.user.login) {
        userId = this.parentGroup.groupOwner.userId;
        username = this.parentGroup.groupOwner.username;
      }
      if (NumberUtils.isNotNumber(this.parentGroup.id) || this.parentGroup.id < 0) {
        this.parentGroup = null;
      }
    }
    if (!this.projectGroup) {
      this.projectGroup = new ProjectGroupVO();
      this.projectGroup.guid = StringUtils.createUuid();
      this.projectGroup.groupType = this.groupType;
      this.projectGroup.parentGroup = this.parentGroup;
      this.projectGroup.groupOwner = new UserInfoVO(userId, username);
      this.projectGroup.isHidden = this.isHidden;
      this.projectGroup.id = -1;
    }
  }

  private async checkForDuplicatesAndContinueToSave(projectGroupsToCheck: ProjectGroupVO[]): Promise<void> {
    // check for already existing group name
    if (await this.groupNameAlreadyExists(projectGroupsToCheck)) {
      return;
    }
    // proceed to save group
    this.projectGroup.groupName = this.groupNameTextInput()?.nativeElement.value;
    this.projectGroup.groupType = this.groupType;
    const projectGroupVO: ProjectGroupVO = this.projectGroup;
    projectGroupVO.id = isNaN(this.projectGroup.id) ? -1 : this.projectGroup.id;
    try {
      const projectGroup = await this.projectGroupService.saveProjectGroup(this.isArticleProjectGroup, projectGroupVO);
      this.finish.emit(projectGroup);
    } catch (error) {
      if (error && NumberUtils.isNumber(error.status) && WagoApiStatusCode.Forbidden === error.status) {
        await this.alertHelper.showError('serverCommunication.title.user_has_no_rights', 'serverCommunication.message.user_has_no_rights');
      } else {
        const message: string = error && error.statusText ? error.statusText : '';
        await this.alertHelper.showError('save.title.error_saving_projectGroup', 'save.message.error_saving_projectGroup', {translationParams: [message]});
      }
    }
    this.close();
  }

  private isEqualProjectGroupType(currentGroup: ProjectGroupVO, existingGroup: ProjectGroupVO): boolean {
    // usual comparison like currentGroup.groupType === existingGroup.groupType is not sufficient here
    // because we have multiple types for normal project-groups (folders) and single type for configuration-group (project)
    if (ProjectGroupUtils.isConfigurationGroup(currentGroup)) {
      return ProjectGroupUtils.isConfigurationGroup(existingGroup);
    }
    if (ProjectGroupUtils.isConfigurationGroup(existingGroup)) {
      return false;
    }
    // treat all other group-types as normal project-group (folder)
    return true;
  }

  public onGroupNameInput(event: any): void {
    if (event.target) {
      const isValid = CustomValidators.checkProjectNameIsValid(event.target.value);
      if (!isValid) {
        this.groupNameError = `validation.error.contains_invalid_character`;
        return;
      }
    }
    this.groupNameError = null;
  }

  private async groupNameAlreadyExists(projectGroupsToCheck: ProjectGroupVO[]): Promise<boolean> {
    if (ArrayUtils.isEmpty(projectGroupsToCheck)) {
      return false;
    }

    for (const projectGroupToCheck of projectGroupsToCheck) {
      if (this.isEqualProjectGroupType(this.projectGroup, projectGroupToCheck) && projectGroupToCheck.groupName === this.groupNameTextInput()?.nativeElement.value
        && projectGroupToCheck.id !== this.projectGroup.id && projectGroupToCheck.groupOwner.userId === this.projectGroup.groupOwner.userId) {
        const errorTitle: string = this.isConfigurationGroupType()
          ? 'save.title.error_saving_configurationGroup'
          : 'save.title.error_saving_projectGroup';
        const errorMessage: string = this.isConfigurationGroupType()
          ? 'save.message.error_configurationtGroup_name_exists'
          : 'save.message.error_projectGroup_name_exists';
        await this.alertHelper.showError(errorTitle, errorMessage);
        return true;
      }
    }

    return false;
  }
}
